import { useState } from "react";
import { useLayoutContext } from "@/components/Layout";
import {
  useCurrentPeriod,
  usePeriods,
  usePeriodEvents,
  type SavingsEvent,
} from "@/api/hooks/use-billing-statements";
import { useTrialStatus } from "@/api/hooks/use-billing-trial";
import { KpiCard } from "@/components/KpiCard";
import { KpiGrid } from "@/components/KpiGrid";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { StatusBadge } from "@/components/StatusBadge";
import { Drawer } from "@/components/Drawer";
import { EventDetailPanel } from "@/components/EventDetailPanel";
import { TrialBanner } from "@/components/TrialBanner";
import { TrialExportCard } from "@/components/TrialExportCard";
import { formatUsd, formatTimestamp } from "@/lib/format";

type SortKey = "created_at" | "verified_savings" | "baseline_cost" | "actual_cost";
type SortDir = "asc" | "desc";

export function CurrentPeriod() {
  const { tenantId } = useLayoutContext();
  const trialQ = useTrialStatus(tenantId);
  const trial = trialQ.data;
  const trialStatus = trial?.trial_status ?? "not_started";
  const currentQ = useCurrentPeriod(tenantId);
  // To get events for the open period, find the draft BillingPeriod (if any).
  const periodsQ = usePeriods(tenantId, "draft");
  const draftPeriod = periodsQ.data?.periods[0] ?? null;
  const eventsQ = usePeriodEvents(draftPeriod?.id ?? null, null, 50);

  const [selected, setSelected] = useState<SavingsEvent | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  if (!tenantId) {
    return <EmptyState message="Sign in to view billing data" />;
  }

  if (trialStatus === "declined" && trial) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-fg">Current Period</h1>
        </div>
        <EmptyState
          message={`Benchmark declined on ${formatTimestamp(trial.trial_declined_at)}. Your requests are forwarded to your provider unchanged, and nothing is optimized or billed. Contact Phantm at support@getphantm.ai to turn optimization on.`}
        />
      </div>
    );
  }

  // The Benchmark ended with no card on file (finding D81): Pass-through
  // until one is saved, which converts it.
  if (trialStatus === "awaiting_card" && trial) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-fg">Current Period</h1>
        </div>
        <div
          data-testid="awaiting-card"
          className="flex flex-col items-center gap-3 rounded-lg border border-line-subtle bg-surface px-6 py-12 text-center text-sm text-fg-muted"
        >
          <p>
            Benchmark ended on {formatTimestamp(trial.trial_ends_at)} with no card on file. Your requests are
            forwarded to your provider unchanged, and nothing is optimized or billed.
          </p>
          <a
            href="/billing"
            className="rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90"
          >
            Add a card to turn optimization on
          </a>
        </div>
        <TrialExportCard tenantId={tenantId} />
      </div>
    );
  }

  // Active trial → fully trial-specific view. No BillingPeriod exists yet
  // (the aggregator creates one only on conversion), so the regular KPI cards
  // would show zeros and the event list would re-render data the TrialBanner
  // already summarises. Render trial KPIs and stop.
  if (trialStatus === "active" && trial) {
    const totalDays = trialTotalDaysFromResponse(trial);
    const dayOfTrial = Math.min(
      totalDays,
      Math.max(1, totalDays - trial.days_remaining + 1),
    );
    const coveragePct = Math.round(trial.real_shadow_coverage_pct * 100);
    const savingsSubtitle =
      trial.successful_request_count > 0
        ? `${trial.real_shadow_request_count} of ${trial.successful_request_count} successful requests shadowed`
        : "Waiting for your first request";
    // Match the TrialBanner hero: show "Calculating…" until the first
    // shadow run lands, otherwise show the projected savings value.
    const savingsValue =
      trial.shadowed_request_count === 0
        ? "Calculating…"
        : `${formatUsd(trial.projected_savings_usd)} (${Math.round(
            trial.projected_savings_pct * 100,
          )}%)`;
    return (
      <div className="space-y-6">
        <TrialBanner trial={trial} tenantId={tenantId} />
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-off-white">Current Period</h1>
        </div>
        <div className="rounded-lg border border-steel/20 bg-ink p-5">
          <KpiGrid>
            <KpiCard
              testId="trial-projected-savings-kpi"
              label="Projected savings (trial)"
              value={savingsValue}
              color="green"
              subtitle={savingsSubtitle}
            />
            <KpiCard
              testId="trial-day-counter-kpi"
              label={`Day ${dayOfTrial} of ${totalDays}`}
              value={`${trial.days_remaining}d remaining`}
            />
            <KpiCard
              testId="trial-auto-converts-kpi"
              label="Auto-converts on"
              value={formatTimestamp(trial.trial_ends_at)}
            />
            {trial.cap_reached ? (
              <KpiCard
                testId="trial-cap-reached-kpi"
                label="Status"
                value="Frozen"
                subtitle="Analysis budget reached"
                color="amber"
              />
            ) : (
              <KpiCard
                testId="trial-coverage-kpi"
                label="Requests shadowed"
                value={`${coveragePct}%`}
                subtitle="Of successful requests"
              />
            )}
          </KpiGrid>
        </div>
      </div>
    );
  }

  if (currentQ.isLoading) {
    return <LoadingSkeleton cards={4} rows={5} />;
  }

  if (currentQ.isError) {
    return (
      <ErrorState
        message={currentQ.error?.message || "Failed to load current period"}
        onRetry={() => currentQ.refetch()}
      />
    );
  }

  const data = currentQ.data!;
  const events = eventsQ.data?.items ?? [];
  const sortedEvents = sortEvents(events, sortKey, sortDir);

  const feeRatePct = (parseFloat(data.savings_fee_rate) * 100).toFixed(0);

  // Active trial is handled by the early return above.
  const trialConverted = trialStatus === "converted" && !!trial;

  return (
    <div className="space-y-6">
      {trialConverted && (
        <div
          data-testid="trial-converted-ribbon"
          className="rounded border border-electric-blue/30 bg-electric-blue/5 px-4 py-2 text-xs text-electric-blue"
        >
          Converted from trial on {formatTimestamp(trial.trial_converted_at)}.
          Your normal billing starts now — the trial week was free.
        </div>
      )}

      {trialConverted && <TrialExportCard tenantId={tenantId} />}

      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-fg">Current Period</h1>
        <StatusBadge status={data.status} />
      </div>

      <div className="rounded-lg border border-line-subtle bg-surface p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-fg-muted">
            {data.period_start} &mdash; {data.period_end} (month to date)
          </p>
          <a
            href={data.methodology_doc_url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs text-electric-blue hover:underline"
          >
            Methodology {data.methodology_version}
          </a>
        </div>

        <KpiGrid>
          <KpiCard
            testId="verified-savings-kpi"
            label="Verified Savings"
            value={formatUsd(data.total_verified_savings)}
            color="green"
            subtitle={`${data.billable_requests.toLocaleString()} billable requests`}
          />
          <KpiCard
            label="Projected Invoice"
            value={formatUsd(data.projected_total_invoice)}
            subtitle={`${feeRatePct}% of verified savings`}
          />
          <KpiCard
            label="Actual LLM Spend"
            value={formatUsd(data.total_actual_cost)}
            subtitle={`Baseline ${formatUsd(data.total_baseline_cost)}`}
          />
        </KpiGrid>

        <div className="rounded border border-line-subtle bg-canvas px-4 py-3 space-y-1">
          <p className="text-xs font-medium text-fg-muted uppercase tracking-wider mb-2">
            Fee math
          </p>
          <FeeMathRow
            label={`Verified Savings × Fee Rate (${feeRatePct}%)`}
            value={formatUsd(data.projected_savings_fee)}
          />
          <div className="border-t border-line-subtle mt-2 pt-2 flex justify-between text-sm font-medium">
            <span className="text-fg">Projected total</span>
            <span className="text-fg font-mono">
              {formatUsd(data.projected_total_invoice)}
            </span>
          </div>
        </div>
      </div>

      {/* Recent events */}
      <div className="rounded-lg border border-line-subtle bg-surface">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line-subtle">
          <h2 className="text-sm font-medium text-fg">Recent Events</h2>
          <span className="text-xs text-fg-muted">
            {events.length > 0
              ? `Showing ${events.length} most recent`
              : "No events yet"}
          </span>
        </div>
        {eventsQ.isLoading ? (
          <div className="px-4 py-6">
            <LoadingSkeleton rows={5} />
          </div>
        ) : events.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-fg-muted">
            No savings events recorded for this period yet.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-line-subtle text-fg-muted">
                <SortHeader
                  label="Created"
                  active={sortKey === "created_at"}
                  dir={sortDir}
                  onClick={() => toggleSort("created_at", sortKey, sortDir, setSortKey, setSortDir)}
                />
                <th className="px-4 py-2 text-left font-medium">Models (baseline → actual)</th>
                <SortHeader
                  label="Baseline"
                  align="right"
                  active={sortKey === "baseline_cost"}
                  dir={sortDir}
                  onClick={() => toggleSort("baseline_cost", sortKey, sortDir, setSortKey, setSortDir)}
                />
                <SortHeader
                  label="Actual"
                  align="right"
                  active={sortKey === "actual_cost"}
                  dir={sortDir}
                  onClick={() => toggleSort("actual_cost", sortKey, sortDir, setSortKey, setSortDir)}
                />
                <SortHeader
                  label="Savings"
                  align="right"
                  active={sortKey === "verified_savings"}
                  dir={sortDir}
                  onClick={() => toggleSort("verified_savings", sortKey, sortDir, setSortKey, setSortDir)}
                />
                <th className="px-4 py-2 text-left font-medium">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {sortedEvents.map((ev) => (
                <tr
                  key={ev.id}
                  className="hover:bg-surface-2 cursor-pointer"
                  onClick={() => setSelected(ev)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelected(ev);
                    }
                  }}
                >
                  <td className="px-4 py-2 text-fg-muted font-mono whitespace-nowrap">
                    {formatTimestamp(ev.created_at)}
                  </td>
                  <td className="px-4 py-2 text-fg">
                    <span className="text-fg-muted">{ev.baseline_model}</span>
                    <span className="mx-1 text-fg-faint">→</span>
                    <span>{ev.actual_model}</span>
                  </td>
                  <td className="px-4 py-2 text-right text-fg-muted font-mono">
                    {formatUsd(ev.baseline_cost)}
                  </td>
                  <td className="px-4 py-2 text-right text-fg font-mono">
                    {formatUsd(ev.actual_cost)}
                  </td>
                  <td className="px-4 py-2 text-right text-safe-green font-mono">
                    {formatUsd(ev.verified_savings)}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={ev.confidence} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Savings event"
      >
        {selected && <EventDetailPanel event={selected} />}
      </Drawer>
    </div>
  );
}

function trialTotalDaysFromResponse(trial: {
  trial_started_at: string | null;
  trial_ends_at: string | null;
}): number {
  if (!trial.trial_started_at || !trial.trial_ends_at) return 7;
  const start = new Date(trial.trial_started_at).getTime();
  const end = new Date(trial.trial_ends_at).getTime();
  if (isNaN(start) || isNaN(end) || end <= start) return 7;
  return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
}


function FeeMathRow({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-fg-muted">{label}</span>
      <span className={`${muted ? "text-fg-muted" : "text-fg"} font-mono`}>
        {value}
      </span>
    </div>
  );
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
  align = "left",
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: "left" | "right";
}) {
  const indicator = active ? (dir === "asc" ? "▲" : "▼") : "";
  return (
    <th
      className={`px-4 py-2 font-medium cursor-pointer select-none hover:text-fg transition-colors ${
        align === "right" ? "text-right" : "text-left"
      }`}
      onClick={onClick}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-[10px]">{indicator}</span>
      </span>
    </th>
  );
}

function toggleSort(
  key: SortKey,
  currentKey: SortKey,
  currentDir: SortDir,
  setKey: (k: SortKey) => void,
  setDir: (d: SortDir) => void,
) {
  if (key === currentKey) {
    setDir(currentDir === "asc" ? "desc" : "asc");
  } else {
    setKey(key);
    setDir("desc");
  }
}

function sortEvents(
  events: SavingsEvent[],
  key: SortKey,
  dir: SortDir,
): SavingsEvent[] {
  const sorted = [...events].sort((a, b) => {
    let av: number | string;
    let bv: number | string;
    if (key === "created_at") {
      av = a.created_at ?? "";
      bv = b.created_at ?? "";
    } else {
      av = parseFloat(a[key]);
      bv = parseFloat(b[key]);
    }
    if (av < bv) return -1;
    if (av > bv) return 1;
    return 0;
  });
  return dir === "asc" ? sorted : sorted.reverse();
}
